/**
 * Low-level HTTP request primitive for wsr-product-admin.
 *
 * Responsibilities (mirroring saas-admin/src/api/client.ts adapted for
 * the wsr-product-admin surface):
 *
 *  - Base URL resolution: auth base for `/api/auth/**` and
 *    `/api/product-admin/**`, wsr-api base for `/admin/platform/**`.
 *  - Per-path credentials (auth invariant #2): `withCredentials: true` to the
 *    auth origin, `false` to the bearer-only wsr-api base (WSRPDM-0031).
 *  - `X-Request-ID` for traceability.
 *  - CSRF double-submit on every saas-auth path the shipped
 *    `CsrfDoubleSubmitFilter` protects: `/api/auth/refresh`,
 *    `/api/auth/logout`, `/api/account/**`, and `/api/product-admin/**`.
 *  - Bearer header attached to `/admin/platform/**`, `/api/account/**`, and
 *    `/api/product-admin/**`.
 *  - 401 → one silent refresh → retry the original call once. Bounded.
 *  - Optional blob responses for attachment downloads, using the same bearer
 *    and bounded 401-refresh path as JSON requests.
 *  - Error normalization through ApiError.
 *
 * Built on the canonical axios factory pattern (extend, do not fork).
 * Tokens are in memory only (auth invariant #1 — never localStorage/
 * sessionStorage).
 */
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ApiError, apiErrorFromEnvelope, type ErrorCode } from './errors';
import { fetchCsrfToken } from './csrf';
import { generateRequestId } from '@/utils/uuid';
import { apiBase } from '@/config/platform';
import { getBootstrap } from '@/config/bootstrap';
import { getAccessToken, setAccessToken, setCsrfToken, signOut } from '@/auth/authStore';
import type { ApiResponse, AuthTokenResponse } from './types';

const CSRF_PROTECTED_EXACT = new Set<string>(['/api/auth/refresh', '/api/auth/logout']);
const CSRF_PROTECTED_PREFIXES: readonly string[] = ['/api/account/', '/api/product-admin/'];

/**
 * The wsr-api surface. `baseUrlFor` and `sendCredentialsFor` MUST agree on this
 * prefix: a path routed to wsr-api but not recognised by the credential
 * predicate would be sent credentialed and fail CORS preflight (WSRPDM-0031).
 * One constant so they cannot drift.
 */
const WSR_API_PREFIX = '/admin/platform/';

/**
 * Refresh/logout/account/product-admin paths: read the CSRF token via csrf.ts
 * (cookie or GET /api/auth/csrf fallback) and echo it in BOTH the
 * X-CSRF-Token header AND the body's `csrfToken` field (the bootstrap
 * convention).
 */
function isCsrfProtected(path: string): boolean {
  if (CSRF_PROTECTED_EXACT.has(path)) return true;
  return CSRF_PROTECTED_PREFIXES.some((p) => path.startsWith(p));
}

function isRefreshPath(path: string): boolean {
  return path === '/api/auth/refresh';
}

function isBearerPath(path: string): boolean {
  return (
    path.startsWith(WSR_API_PREFIX) ||
    path.startsWith('/api/account/') ||
    path.startsWith('/api/product-admin/')
  );
}

export function baseUrlFor(path: string): string {
  if (path.startsWith(WSR_API_PREFIX)) return apiBase();
  return getBootstrap().authOrigin;
}

/**
 * Per-path credential posture. Cookies travel to the auth origin only — the
 * refresh cookie is HttpOnly and ambient there. wsr-api authenticates by bearer
 * token alone, has no use for cookies, and deliberately does not emit
 * `Access-Control-Allow-Credentials` (WSRAPI-0048); sending credentials to it
 * makes the browser demand that header and fails every preflight.
 */
function sendCredentialsFor(path: string): boolean {
  return !path.startsWith(WSR_API_PREFIX);
}

/**
 * Per-call metadata threaded onto the axios request config so the request
 * interceptor can inject the bearer from the original `path` (not the
 * resolved URL). Lives under a private key so it never collides with axios'
 * own config fields.
 */
interface RequestMeta {
  path: string;
}

interface AdminRequestConfig extends InternalAxiosRequestConfig {
  __meta?: RequestMeta;
}

/**
 * Shared axios instance. `withCredentials: true` here is the fallback default
 * for any request that somehow reaches `httpClient` without `__meta` — actual
 * calls get their credential posture set per-path by the request interceptor
 * (`sendCredentialsFor`, WSRPDM-0031): credentialed to the auth origin,
 * uncredentialed to the bearer-only wsr-api base. `validateStatus` is disabled
 * so a non-2xx status resolves rather than throwing — the 401/403 retry logic
 * and `parseResponse` below own all status handling. `transformResponse` is
 * the identity so the body arrives as a raw string and JSON/non-JSON parsing
 * is uniform.
 */
export const httpClient: AxiosInstance = axios.create({
  withCredentials: true,
  validateStatus: () => true,
  responseType: 'text',
  transformResponse: [(data: unknown) => data],
});

// Request interceptor: bearer injection for resource-server paths.
// Accept/X-Request-ID and the refresh/logout/account/product-admin CSRF
// header+body are set in `request()` because they depend on the per-call
// request id and the JSON body merge.
httpClient.interceptors.request.use((config: AdminRequestConfig) => {
  const meta = config.__meta;
  if (!meta) return config;
  const { path } = meta;

  config.withCredentials = sendCredentialsFor(path);

  if (isBearerPath(path)) {
    const token = getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

let refreshInFlight: Promise<boolean> | null = null;

export function __resetClientState(): void {
  refreshInFlight = null;
}

async function silentRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const body = await request<AuthTokenResponse>('POST', '/api/auth/refresh', {
        json: {},
        _isRefresh: true,
      });
      if (body.accessToken) {
        setAccessToken(body.accessToken, body.expiresAt ?? null);
        // Retain the optional csrfToken from the flat refresh response in the
        // in-memory auth state; wsr-api requests do not consume it.
        setCsrfToken(body.csrfToken ?? null);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export interface RequestOptions {
  json?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
  responseType?: 'json' | 'blob';
  onResponseHeaders?: (headers: AxiosResponse['headers']) => void;
  _isRefresh?: boolean;
  _isRetry?: boolean;
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const baseUrl = baseUrlFor(path);
  const url = buildUrl(baseUrl, path, options.query);
  const requestId = generateRequestId();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-ID': requestId,
    ...options.headers,
  };

  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // When sending formData, do NOT set Content-Type — the browser sets
  // multipart/form-data with the correct boundary automatically.

  // Bearer headers are attached by the request interceptor off `__meta`
  // below.

  // CSRF double-submit (refresh/logout/account/product-admin): header + body
  // field, token sourced from csrf.ts (cookie-first; GET /api/auth/csrf
  // fallback).
  let csrfValue: string | undefined;
  const needsCsrf = method !== 'GET' && isCsrfProtected(path);
  if (needsCsrf) {
    csrfValue = await fetchCsrfToken(baseUrl);
    headers['X-CSRF-Token'] = csrfValue;
  }

  // Build the request body.
  let data: FormData | string | undefined;
  if (options.formData !== undefined) {
    data = options.formData;
    delete headers['Content-Type'];
  } else if (options.json !== undefined) {
    // If this is a CSRF-protected call, merge csrfToken into the body
    // (refresh/logout expect it per contract).
    let payload: unknown = options.json;
    if (needsCsrf && csrfValue && typeof payload === 'object' && payload !== null) {
      payload = { ...(payload as Record<string, unknown>), csrfToken: csrfValue };
    } else if (needsCsrf && csrfValue && options.json === undefined) {
      payload = { csrfToken: csrfValue };
    }
    data = JSON.stringify(payload);
  } else if (needsCsrf && csrfValue) {
    headers['Content-Type'] = 'application/json';
    data = JSON.stringify({ csrfToken: csrfValue });
  }

  const meta: RequestMeta = { path };

  let res: AxiosResponse<string | Blob>;
  try {
    res = await httpClient.request<string | Blob>({
      url,
      method,
      headers,
      data,
      responseType: options.responseType === 'blob' ? 'blob' : 'text',
      // Marker consumed by the request interceptor for bearer injection.
      __meta: meta,
    } as AdminRequestConfig);
  } catch {
    throw new ApiError('NETWORK_ERROR', 0, { requestId });
  }

  // 401 → silent refresh → retry once. Only on bearer paths; refresh itself
  // is excluded by the guards.
  if (
    res.status === 401 &&
    isBearerPath(path) &&
    !options._isRefresh &&
    !options._isRetry &&
    !isRefreshPath(path)
  ) {
    const ok = await silentRefresh();
    if (!ok) {
      signOut();
      throw new ApiError('SESSION_EXPIRED', 401, { requestId });
    }
    return request<T>(method, path, { ...options, _isRetry: true });
  }

  options.onResponseHeaders?.(res.headers);
  return parseResponse<T>(res, requestId, options.responseType ?? 'json');
}

function parseResponse<T>(
  res: AxiosResponse<string | Blob>,
  requestId: string,
  responseType: 'json' | 'blob',
): T {
  const headerRequestId = res.headers['x-request-id'];
  const responseRequestId = typeof headerRequestId === 'string' ? headerRequestId : requestId;

  if (res.status === 204) {
    return undefined as T;
  }

  const ok = res.status >= 200 && res.status < 300;
  if (responseType === 'blob') {
    if (!ok) {
      throw new ApiError(statusToCode(res.status), res.status, {
        requestId: responseRequestId,
      });
    }
    return res.data as T;
  }

  const text = typeof res.data === 'string' ? res.data : '';
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      if (!ok) {
        throw new ApiError(statusToCode(res.status), res.status, {
          requestId: responseRequestId,
          details: { message: text },
        });
      }
      throw new ApiError('UNKNOWN', res.status, {
        requestId: responseRequestId,
        details: { message: 'Non-JSON response', raw: text },
      });
    }
  }

  if (!ok) {
    throw apiErrorFromEnvelope(res.status, body, responseRequestId);
  }
  return body as T;
}

function statusToCode(status: number): ErrorCode {
  if (status === 401) return 'SESSION_EXPIRED';
  if (status === 403) return 'TENANT_ORIGIN_MISMATCH';
  if (status === 429) return 'RATE_LIMIT_REJECTED';
  return 'UNKNOWN';
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, string | number | undefined>,
): string {
  const raw = baseUrl.replace(/\/+$/, '') + path;
  // When baseUrl is a same-origin relative path (no scheme), resolve against
  // location.origin so the URL object supports query-param manipulation.
  // Auth-origin calls supply an absolute base; wsr-api (WSRPDM-0072) is relative.
  const url = raw.startsWith('http') ? new URL(raw) : new URL(raw, location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.append(k, String(v));
    }
  }
  return url.toString();
}

/**
 * Unwrap the standard ApiResponse<T> envelope. Admin endpoints return
 * `{ success, message, data }`; callers want the data.
 */
export function unwrap<T>(response: ApiResponse<T>): T {
  return response.data;
}
